"use client"

import { useState, useCallback, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Copy, RefreshCw, Trash2 } from "lucide-react"
import { toast } from "sonner"

const LOWERCASE = "abcdefghijklmnopqrstuvwxyz"
const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
const NUMBERS = "0123456789"
const SYMBOLS = "!@#$%^&*()_+-=[]{}|;:,.<>?"

const WORD_LIST = [
  "apple","arrow","badge","beach","blade","blaze","bloom","board","brain","brave",
  "bread","brick","bring","broad","brush","burst","cable","candy","chain","chair",
  "chase","cheap","chest","chief","chill","claim","clash","class","clean","clear",
  "click","cliff","climb","clock","close","cloud","coach","coast","coral","couch",
  "count","cover","crack","craft","crane","crash","crazy","cream","creek","crest",
  "crowd","crown","crush","curve","cycle","dance","deals","debug","delta","depth",
  "diary","digit","dodge","dolly","donor","doubt","draft","drain","drake","dream",
  "dress","drift","drink","drive","drops","drums","dusty","eager","eagle","earth",
  "eight","elder","ember","empty","enemy","enter","equal","error","event","every",
  "exact","extra","fable","faced","faith","feast","fence","fetch","fiber","field",
  "fight","final","flame","flash","fleet","float","flood","floor","flora","fluid",
  "flute","focus","force","forge","forum","found","frame","frank","fresh","front",
  "frost","fruit","funny","giant","giddy","given","glass","gleam","glide","globe",
  "glory","grace","grade","grain","grand","grant","grape","grasp","grass","grave",
  "great","green","grind","group","grove","guard","guess","guide","guild","habit",
  "happy","haven","heart","heavy","hedge","hello","horse","hotel","house","human",
  "humor","hurry","ideal","image","index","inner","input","ivory","jolly","judge",
  "juice","knock","label","lance","laser","later","layer","learn","level","light",
  "limit","linen","liver","logic","lucky","lunar","magic","major","maker","manor",
  "maple","march","match","mayor","media","mercy","merit","metal","micro","might",
  "minor","model","money","month","moral","motor","mount","mouse","mouth","movie",
  "music","noble","noise","north","noted","novel","nurse","ocean","offer","olive",
  "omega","onset","opera","orbit","order","other","outer","owned","oxide","ozone",
  "paint","panel","paper","party","patch","peace","pearl","penny","phase","phone",
  "photo","piano","piece","pilot","pixel","pizza","place","plain","plane","plant",
  "plate","plaza","plead","plumb","plume","point","polar","pound","power","press",
  "price","pride","prime","print","prior","prize","probe","proof","proud","proxy",
  "pulse","punch","pupil","queen","query","quest","quick","quiet","quote","radar",
  "radio","raise","rally","ranch","range","rapid","reach","react","ready","realm",
  "rebel","reign","relax","reply","rider","ridge","rifle","right","rigid","risky",
  "rival","river","robin","robot","rocky","royal","rural","saint","salad","satin",
  "scale","scene","scope","scout","sense","serve","shade","shake","shame","shape",
  "share","sharp","shell","shift","shine","shirt","shock","shore","short","sigma",
  "sight","silly","since","skill","skull","slash","slate","sleep","slice","slide",
  "small","smart","smile","smoke","snack","snake","solar","solid","solve","sound",
  "south","space","spare","spark","speak","speed","spell","spend","spice","spine",
  "sport","squad","stack","staff","stage","stair","stake","stamp","stand","stark",
  "start","state","stays","steam","steel","steep","steer","stern","stick","still",
  "stock","stone","store","storm","story","stove","strap","straw","strip","study",
  "stuff","style","sugar","suite","sunny","super","surge","swamp","swear","sweep",
  "sweet","swift","swing","sword","table","taste","teach","tempo","theft","theme",
  "thick","thing","think","thorn","three","throw","thumb","tidal","tiger","tight",
  "timer","title","toast","token","topic","total","touch","tough","towel","tower",
  "toxic","trace","track","trade","trail","train","trait","trash","treat","trend",
  "trial","tribe","trick","tried","truck","truly","trump","trunk","trust","truth",
  "tulip","tumor","tuner","twice","twist","ultra","under","union","unite","unity",
  "until","upper","upset","urban","usage","usual","utter","valid","value","vapor",
  "vault","verse","video","vigor","vinyl","viola","viral","visit","vista","vital",
  "vivid","vocal","voice","voter","vowel","wages","waste","watch","water","weary",
  "weave","weird","wheat","wheel","where","which","while","white","whole","wider",
  "witch","woman","world","worry","worse","worst","worth","would","wound","wrist",
  "write","wrong","yacht","young","youth","zebra","zones",
]

const SEPARATORS = ["-", ".", "_", " ", "!", "@", "#"]

interface Options {
  length: number
  lowercase: boolean
  uppercase: boolean
  numbers: boolean
  symbols: boolean
  excludeAmbiguous: boolean
}

interface PassphraseOptions {
  wordCount: number
  separator: string
  capitalize: boolean
  includeNumber: boolean
}

function calculateEntropy(password: string, mode: "password" | "passphrase", opts?: { charsetSize?: number; wordCount?: number }): number {
  if (mode === "passphrase" && opts?.wordCount) {
    const wordListSize = WORD_LIST.length
    const entropy = opts.wordCount * Math.log2(wordListSize)
    return Math.round(entropy * 10) / 10
  }

  if (opts?.charsetSize && opts.charsetSize > 0) {
    return Math.round(password.length * Math.log2(opts.charsetSize) * 10) / 10
  }
  return 0
}

function getEntropyLabel(bits: number): { text: string; color: string } {
  if (bits < 40) return { text: "Very Weak", color: "bg-red-500" }
  if (bits < 60) return { text: "Weak", color: "bg-orange-500" }
  if (bits < 80) return { text: "Fair", color: "bg-yellow-500" }
  if (bits < 100) return { text: "Strong", color: "bg-green-500" }
  if (bits < 128) return { text: "Very Strong", color: "bg-emerald-500" }
  return { text: "Excellent", color: "bg-emerald-600" }
}

export function PasswordGenerator() {
  const [mode, setMode] = useState<"password" | "passphrase">("password")
  const [passwords, setPasswords] = useState<string[]>([])
  const [count, setCount] = useState(1)
  const [options, setOptions] = useState<Options>({
    length: 16,
    lowercase: true,
    uppercase: true,
    numbers: true,
    symbols: true,
    excludeAmbiguous: false,
  })
  const [passphraseOptions, setPassphraseOptions] = useState<PassphraseOptions>({
    wordCount: 5,
    separator: "-",
    capitalize: true,
    includeNumber: false,
  })

  const charsetSize = useMemo(() => {
    let size = 0
    if (options.lowercase) size += 26
    if (options.uppercase) size += 26
    if (options.numbers) size += 10
    if (options.symbols) size += SYMBOLS.length
    if (options.excludeAmbiguous) size -= 6
    return Math.max(0, size)
  }, [options])

  const generatePassword = useCallback((opts: Options): string => {
    let charset = ""
    if (opts.lowercase) charset += LOWERCASE
    if (opts.uppercase) charset += UPPERCASE
    if (opts.numbers) charset += NUMBERS
    if (opts.symbols) charset += SYMBOLS

    if (opts.excludeAmbiguous) {
      charset = charset.replace(/[0OIl1|]/g, "")
    }

    if (!charset) return ""

    const array = new Uint32Array(opts.length)
    crypto.getRandomValues(array)

    return Array.from(array, (x) => charset[x % charset.length]).join("")
  }, [])

  const generatePassphrase = useCallback((opts: PassphraseOptions): string => {
    const array = new Uint32Array(opts.wordCount)
    crypto.getRandomValues(array)

    let words = Array.from(array, (x) => WORD_LIST[x % WORD_LIST.length])

    if (opts.capitalize) {
      words = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    }

    let result = words.join(opts.separator)

    if (opts.includeNumber) {
      const numArray = new Uint32Array(1)
      crypto.getRandomValues(numArray)
      result += opts.separator + (numArray[0] % 1000).toString()
    }

    return result
  }, [])

  const generate = useCallback(() => {
    if (mode === "password") {
      if (!options.lowercase && !options.uppercase && !options.numbers && !options.symbols) {
        toast.error("Please select at least one character type")
        return
      }
      const newPasswords = Array.from({ length: count }, () => generatePassword(options))
      setPasswords(newPasswords)
    } else {
      const newPassphrases = Array.from({ length: count }, () => generatePassphrase(passphraseOptions))
      setPasswords(newPassphrases)
    }
  }, [count, mode, options, passphraseOptions, generatePassword, generatePassphrase])

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
    toast.success("Copied to clipboard!")
  }, [])

  const copyAll = useCallback(() => {
    navigator.clipboard.writeText(passwords.join("\n"))
    toast.success("All passwords copied to clipboard!")
  }, [passwords])

  const clear = useCallback(() => {
    setPasswords([])
  }, [])

  const currentEntropy = useMemo(() => {
    if (passwords.length === 0) return 0
    if (mode === "passphrase") {
      return calculateEntropy(passwords[0], "passphrase", { wordCount: passphraseOptions.wordCount + (passphraseOptions.includeNumber ? 1 : 0) })
    }
    return calculateEntropy(passwords[0], "password", { charsetSize })
  }, [passwords, mode, charsetSize, passphraseOptions])

  const entropyLabel = getEntropyLabel(currentEntropy)

  useEffect(() => {
    let active = true
    queueMicrotask(() => {
      if (!active) return
      setPasswords([
        generatePassword({
          length: 16,
          lowercase: true,
          uppercase: true,
          numbers: true,
          symbols: true,
          excludeAmbiguous: false,
        }),
      ])
    })

    return () => {
      active = false
    }
  }, [generatePassword])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Password Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={mode} onValueChange={(v) => setMode(v as "password" | "passphrase")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password">Random Password</TabsTrigger>
              <TabsTrigger value="passphrase">Passphrase</TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="space-y-6 pt-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Length: {options.length}</Label>
                </div>
                <Slider
                  value={[options.length]}
                  onValueChange={([value]) => setOptions({ ...options, length: value })}
                  min={4}
                  max={128}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="lowercase"
                    checked={options.lowercase}
                    onCheckedChange={(checked) =>
                      setOptions({ ...options, lowercase: checked as boolean })
                    }
                  />
                  <Label htmlFor="lowercase">Lowercase (a-z)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="uppercase"
                    checked={options.uppercase}
                    onCheckedChange={(checked) =>
                      setOptions({ ...options, uppercase: checked as boolean })
                    }
                  />
                  <Label htmlFor="uppercase">Uppercase (A-Z)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="numbers"
                    checked={options.numbers}
                    onCheckedChange={(checked) =>
                      setOptions({ ...options, numbers: checked as boolean })
                    }
                  />
                  <Label htmlFor="numbers">Numbers (0-9)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="symbols"
                    checked={options.symbols}
                    onCheckedChange={(checked) =>
                      setOptions({ ...options, symbols: checked as boolean })
                    }
                  />
                  <Label htmlFor="symbols">Symbols (!@#$...)</Label>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="excludeAmbiguous"
                  checked={options.excludeAmbiguous}
                  onCheckedChange={(checked) =>
                    setOptions({ ...options, excludeAmbiguous: checked as boolean })
                  }
                />
                <Label htmlFor="excludeAmbiguous">Exclude ambiguous characters (0, O, I, l, 1, |)</Label>
              </div>
            </TabsContent>

            <TabsContent value="passphrase" className="space-y-6 pt-2">
              <div className="space-y-2">
                <Label>Word Count: {passphraseOptions.wordCount}</Label>
                <Slider
                  value={[passphraseOptions.wordCount]}
                  onValueChange={([value]) =>
                    setPassphraseOptions({ ...passphraseOptions, wordCount: value })
                  }
                  min={3}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label>Word Separator</Label>
                <Select
                  value={passphraseOptions.separator}
                  onValueChange={(v) =>
                    setPassphraseOptions({ ...passphraseOptions, separator: v })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEPARATORS.map((sep) => (
                      <SelectItem key={sep} value={sep}>
                        {sep === " " ? "Space" : `"${sep}"`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="capitalize"
                    checked={passphraseOptions.capitalize}
                    onCheckedChange={(checked) =>
                      setPassphraseOptions({ ...passphraseOptions, capitalize: checked as boolean })
                    }
                  />
                  <Label htmlFor="capitalize">Capitalize Words</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeNumber"
                    checked={passphraseOptions.includeNumber}
                    onCheckedChange={(checked) =>
                      setPassphraseOptions({ ...passphraseOptions, includeNumber: checked as boolean })
                    }
                  />
                  <Label htmlFor="includeNumber">Append Number</Label>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {passwords.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Entropy</span>
                <span className={`px-2 py-0.5 rounded text-white text-xs font-medium ${entropyLabel.color}`}>
                  {entropyLabel.text} ({currentEntropy} bits)
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all rounded-full ${entropyLabel.color}`}
                  style={{ width: `${Math.min(100, (currentEntropy / 128) * 100)}%` }}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Number of passwords: {count}</Label>
            <Slider
              value={[count]}
              onValueChange={([value]) => setCount(value)}
              min={1}
              max={20}
              step={1}
              className="w-full"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={generate} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate
            </Button>
            {passwords.length > 0 && (
              <>
                <Button variant="outline" onClick={copyAll}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All
                </Button>
                <Button variant="outline" onClick={clear}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {passwords.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Generated {mode === "passphrase" ? "Passphrases" : "Passwords"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {passwords.map((password, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted rounded-md font-mono text-sm"
                >
                  <span className="break-all select-all">{password}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(password)}
                    className="ml-2 flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
